import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, LinkSimple, Copy, ArrowsClockwise, Prohibit, PencilSimple, Check, X,
  Eye, DownloadSimple, ClockCounterClockwise, ShieldCheck, WarningCircle, Trash, CaretRight, ChartLineUp,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { money, dateTime, relativeTime } from "../../lib/format";
import { computePayment } from "../../lib/calc";
import { ALL_STAGES, PIPELINE_ORDER, stageMeta, docStatusMeta } from "../../lib/stages";
import { useAuth } from "../../context/AuthContext";
import { StatusChip } from "../../components/StatusChip";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

export default function DealDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isOwner } = useAuth();
  const [deal, setDeal] = useState(null);
  const [audit, setAudit] = useState([]);
  const [rejectDoc, setRejectDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [linkBase, setLinkBase] = useState("");

  const load = useCallback(() => {
    api.get(`/deals/${id}`).then((r) => setDeal(r.data)).catch(() => toast.error("Deal not found"));
    api.get(`/deals/${id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  }, [id]);
  useEffect(load, [load]);
  useEffect(() => { api.get("/settings").then((r) => setLinkBase((r.data.public_base_url || "").replace(/\/$/, ""))).catch(() => {}); }, []);

  if (!deal)
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-4">
        <div className="h-8 w-64 bg-card rounded animate-pulse" />
        <div className="h-40 bg-card rounded-xl animate-pulse" />
        <div className="h-64 bg-card rounded-xl animate-pulse" />
      </div>
    );

  const meta = stageMeta(deal.stage);
  const preview = deal.payment_preview || computePayment(deal.terms);
  const link = `${linkBase || window.location.origin}/offer/${deal.security?.token}`;
  const revoked = deal.security?.revoked;

  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };

  const changeStage = async (stage) => {
    await api.post(`/deals/${id}/stage`, { stage });
    toast.success(`Moved to ${stageMeta(stage).label}`);
    load();
  };
  const regenerate = async () => {
    await api.post(`/deals/${id}/regenerate-link`);
    toast.success("Link regenerated — old link invalidated");
    load();
  };
  const revoke = async () => {
    await api.post(`/deals/${id}/revoke-link`);
    toast.success("Link revoked");
    load();
  };
  const approve = async (docId) => {
    await api.post(`/deals/${id}/documents/${docId}/approve`);
    toast.success("Document approved");
    load();
  };
  const drawAction = async (drawId, action, label) => {
    await api.post(`/deals/${id}/draws/${drawId}/${action}`);
    toast.success(label);
    load();
  };
  const submitReject = async () => {
    if (!rejectReason.trim()) return toast.error("Enter a rejection reason");
    await api.post(`/deals/${id}/documents/${rejectDoc}/reject`, { reason: rejectReason });
    toast.success("Document rejected");
    setRejectDoc(null); setRejectReason(""); load();
  };
  const viewFile = async (fileId, download) => {
    try {
      const res = await api.get(`/deals/${id}/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement("a"); a.href = url; a.download = "document"; a.click();
      } else window.open(url, "_blank");
    } catch { toast.error("Could not open file"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/deals/${id}`); toast.success("Deal deleted"); nav("/admin"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ev = (t) => audit.filter((a) => a.event_type === t);
  const openedEvts = ev("link_opened");
  const viewedCount = ev("terms_viewed").length;
  const uploadCount = ev("file_uploaded").length;
  const failedPins = ev("pin_attempt").length;
  const acceptedEvt = ev("offer_accepted")[0];
  const createdEvt = [...audit].reverse().find((a) => a.event_type === "created");
  let timeToAccept = null;
  if (acceptedEvt && createdEvt) {
    const ms = new Date(acceptedEvt.created_at) - new Date(createdEvt.created_at);
    if (ms > 0) {
      const h = Math.floor(ms / 3600000);
      timeToAccept = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${Math.floor((ms % 3600000) / 60000)}m`;
    }
  }
  const lastOpened = openedEvts[0]?.created_at;

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <button onClick={() => nav("/admin")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="detail-back">
        <ArrowLeft size={16} /> Pipeline
      </button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl font-bold tracking-tight">{deal.business?.dba || deal.business?.legal_name}</h1>
            <StatusChip label={meta.label} color={meta.color} bg={meta.bg} testId="detail-stage-chip" />
          </div>
          <p className="text-muted-foreground text-sm mt-1">{deal.business?.legal_name} · {deal.business?.entity_type} · {deal.business?.industry}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={deal.stage} onValueChange={changeStage}>
            <SelectTrigger className="w-44 bg-card border-border h-10" data-testid="stage-control"><SelectValue /></SelectTrigger>
            <SelectContent>{ALL_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => nav(`/admin/deals/${id}/edit`)} data-testid="edit-deal-button"><PencilSimple size={16} className="mr-1" /> Edit</Button>
          {isOwner && (
            <Button variant="outline" className="border-destructive/30 text-destructive hover:bg-destructive/10" onClick={() => setDeleteOpen(true)} data-testid="delete-deal-button">
              <Trash size={16} />
            </Button>
          )}
        </div>
      </div>

      {deal.terms_updated_after_acceptance && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-500" data-testid="terms-updated-banner">
          <WarningCircle size={18} weight="fill" /> Terms were edited after the merchant accepted. The merchant portal shows an "Updated" badge.
        </div>
      )}

      {/* Share card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-[var(--radius)] border border-primary/25 bg-primary/[0.05] p-5 card-inner-glow">
        <div className="flex items-center gap-2 overline text-primary mb-3"><LinkSimple size={15} /> Secure Merchant Link</div>
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 min-w-0">
            <span className={`text-sm truncate ${revoked ? "line-through text-muted-foreground" : ""}`} data-testid="share-link">{link}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => copy(link, "Link copied")} data-testid="copy-link-btn" disabled={revoked}><Copy size={16} className="mr-1" /> Copy link</Button>
            <Button variant="outline" onClick={regenerate} data-testid="regenerate-link-btn"><ArrowsClockwise size={16} className="mr-1" /> Regenerate</Button>
            {revoked ? (
              <span className="text-sm text-destructive font-medium px-2">Revoked</span>
            ) : (
              <Button variant="outline" className="border-destructive/30 text-destructive hover:bg-destructive/10" onClick={revoke} data-testid="revoke-link-btn"><Prohibit size={16} className="mr-1" /> Revoke</Button>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="overline text-muted-foreground">Access PIN</span>
          <span className="text-sm text-muted-foreground">Set per deal · delivered manually.
            Demo PIN for seeded deals is <span className="num text-foreground">1234</span>.</span>
        </div>
      </motion.div>

      <Tabs defaultValue="terms" className="mt-8">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="terms" data-testid="tab-terms">Terms</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="draws" data-testid="tab-draws">Draws</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        {/* TERMS */}
        <TabsContent value="terms" className="mt-5">
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-[var(--radius)] border border-border bg-card p-6">
              <div className="overline text-muted-foreground mb-4">Line of Credit Terms</div>
              <div className="mb-6">
                <div className="text-xs text-muted-foreground">Approved Credit Limit</div>
                <div className="num text-4xl font-bold text-primary">{money(deal.terms?.credit_limit)}</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {(deal.terms?.pricing_mode === "apr" ? Number(deal.terms?.apr) : Number(deal.terms?.factor_rate)) > 0 ? (
                  <Term label="Pricing" value={deal.terms?.pricing_mode === "apr" ? `${deal.terms?.apr}% APR` : `${deal.terms?.factor_rate}x factor`} />
                ) : (
                  <Term label="Pricing" value="No rate (hidden from merchant)" />
                )}
                <Term label="Payment Frequency" value={deal.terms?.payment_frequency} cap />
                <Term label="Draw Period" value={`${deal.terms?.draw_period_months} months`} />
                <Term label="Repayment Term" value={`${deal.terms?.repayment_term_value} ${deal.terms?.repayment_term_unit}`} />
                <Term label="Origination Fee" value={deal.terms?.origination_fee_value ? (deal.terms?.origination_fee_type === "pct" ? `${deal.terms?.origination_fee_value}%` : money(deal.terms?.origination_fee_value)) : "—"} />
                <Term label="Draw Fee" value={`${deal.terms?.draw_fee_pct || 0}%`} />
                <Term label="Monthly Maintenance" value={money(deal.terms?.monthly_maintenance_fee)} />
                <Term label="Late Fee" value={money(deal.terms?.late_fee)} />
                <Term label="Wire / ACH Fee" value={money(deal.terms?.wire_ach_fee)} />
                <Term label="Personal Guarantee" value={deal.terms?.personal_guarantee ? "Required" : "Not required"} />
                <Term label="UCC Filing" value={deal.terms?.ucc_filing ? "Yes" : "No"} />
                <Term label="Offer Expiration" value={dateTime(deal.terms?.offer_expiration)} />
              </div>
              {deal.terms?.prepayment_terms && <div className="mt-5 pt-5 border-t border-border"><Term label="Prepayment / Early Payoff" value={deal.terms.prepayment_terms} /></div>}
              {deal.terms?.internal_notes && (
                <div className="mt-5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4">
                  <div className="overline text-amber-500 mb-1">Internal Notes · Not visible to merchant</div>
                  <p className="text-sm text-muted-foreground">{deal.terms.internal_notes}</p>
                </div>
              )}
            </div>

            <div className="space-y-5">
              <div className="rounded-[var(--radius)] border border-border bg-card p-5">
                <div className="overline text-muted-foreground mb-3">Payment Preview</div>
                <div className="num text-3xl font-bold">{money(preview.estimated_payment)}</div>
                <div className="text-xs text-muted-foreground mt-1">per {preview.payment_frequency} · {preview.number_of_payments} payments</div>
                <div className="mt-4 space-y-2 text-sm">
                  <Row l="Total payback" v={money(preview.total_payback)} />
                  {deal.terms?.show_tcc !== false && <Row l="Cost of capital" v={money(preview.total_cost_of_capital)} accent />}
                </div>
              </div>
              {deal.acceptance?.accepted && (
                <div className="rounded-[var(--radius)] border border-primary/25 bg-primary/[0.06] p-5" data-testid="acceptance-record">
                  <div className="flex items-center gap-2 overline text-primary mb-3"><ShieldCheck size={15} weight="fill" /> Acceptance Record</div>
                  <div className="signature-font text-2xl text-foreground">{deal.acceptance.signature_name}</div>
                  <div className="text-xs text-muted-foreground mt-2 space-y-1">
                    <div>Signed {dateTime(deal.acceptance.accepted_at)}</div>
                    <div>IP {deal.acceptance.ip}</div>
                  </div>
                </div>
              )}

              <div className="rounded-[var(--radius)] border border-border bg-card p-5" data-testid="engagement-card">
                <div className="flex items-center gap-2 overline text-muted-foreground mb-4"><ChartLineUp size={15} /> Merchant Engagement</div>
                <div className="space-y-3.5">
                  <EngRow label="Link opened" value={openedEvts.length ? `Yes · ${relativeTime(lastOpened)}` : "Not yet"} good={openedEvts.length > 0} />
                  <EngRow label="Terms viewed" value={`${viewedCount}×`} />
                  <EngRow label="Documents uploaded" value={`${uploadCount}`} />
                  {failedPins > 0 && <EngRow label="Failed PIN attempts" value={`${failedPins}`} warn />}
                  {timeToAccept && <EngRow label="Time to accept" value={timeToAccept} good />}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-5">
          <div className="space-y-3">
            {deal.documents.length === 0 && <p className="text-sm text-muted-foreground">No documents on this deal.</p>}
            {deal.documents.map((doc) => {
              const dm = docStatusMeta(doc.status);
              return (
                <div key={doc.id} className="rounded-xl border border-border bg-card p-4" data-testid={`doc-${doc.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{doc.name}</span>
                        {!doc.required && <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-secondary rounded px-1.5 py-0.5">Optional</span>}
                      </div>
                      {doc.instruction && <p className="text-xs text-muted-foreground mt-0.5">{doc.instruction}</p>}
                      {doc.status === "rejected" && doc.rejection_reason && (
                        <p className="text-xs text-destructive mt-1">Rejected: {doc.rejection_reason}</p>
                      )}
                    </div>
                    <StatusChip label={dm.label} color={dm.color} bg={dm.bg} />
                  </div>
                  {doc.files.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-2">
                      {doc.files.map((f) => (
                        <div key={f.id} className="flex items-center justify-between gap-2 text-sm">
                          <span className="truncate text-muted-foreground">{f.original_filename} · {(f.size / 1024 / 1024).toFixed(2)} MB</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => viewFile(f.id, false)} data-testid={`view-file-${f.id}`}><Eye size={16} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => viewFile(f.id, true)}><DownloadSimple size={16} /></Button>
                          </div>
                        </div>
                      ))}
                      {doc.status !== "approved" && (
                        <div className="flex items-center gap-2 pt-1">
                          <Button size="sm" onClick={() => approve(doc.id)} data-testid={`approve-doc-${doc.id}`} className="h-8"><Check size={15} className="mr-1" /> Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => setRejectDoc(doc.id)} data-testid={`reject-doc-${doc.id}`} className="h-8 border-destructive/30 text-destructive hover:bg-destructive/10"><X size={15} className="mr-1" /> Reject</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* DRAWS */}
        <TabsContent value="draws" className="mt-5">
          {(() => {
            const s = deal.draws_summary || { limit: 0, available: 0, outstanding: 0 };
            const draws = (deal.draws || []).slice().reverse();
            const dmeta = (st) => ({
              requested: { label: "Requested", color: "#FBBF24", bg: "rgba(251,191,36,0.16)" },
              funded: { label: "Funded", color: "#10B981", bg: "rgba(16,185,129,0.16)" },
              declined: { label: "Declined", color: "#F87171", bg: "rgba(248,113,113,0.14)" },
              closed: { label: "Repaid", color: "#94A3B8", bg: "rgba(148,163,184,0.16)" },
            }[st] || { label: st, color: "#94A3B8", bg: "rgba(148,163,184,0.14)" });
            return (
              <div>
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div className="rounded-xl border border-border bg-card p-4"><div className="overline text-muted-foreground">Limit</div><div className="num text-2xl font-bold mt-1">{money(s.limit)}</div></div>
                  <div className="rounded-xl border border-border bg-card p-4"><div className="overline text-muted-foreground">Available</div><div className="num text-2xl font-bold mt-1 text-primary">{money(s.available)}</div></div>
                  <div className="rounded-xl border border-border bg-card p-4"><div className="overline text-muted-foreground">Outstanding</div><div className="num text-2xl font-bold mt-1">{money(s.outstanding)}</div></div>
                </div>
                {deal.stage !== "funded" && (
                  <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-2.5 text-sm text-amber-500 mb-4">
                    Draws unlock for the merchant once this deal is set to <span className="font-semibold">Funded</span>.
                  </div>
                )}
                {draws.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No draw requests yet.</p>
                ) : (
                  <div className="space-y-3">
                    {draws.map((d) => {
                      const m = dmeta(d.status);
                      const p = d.preview || {};
                      return (
                        <div key={d.id} className="rounded-xl border border-border bg-card p-4" data-testid={`admin-draw-${d.id}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="num font-bold text-xl">{money(d.amount)}</div>
                              <div className="text-xs text-muted-foreground">Requested {dateTime(d.requested_at)}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <StatusChip label={m.label} color={m.color} bg={m.bg} />
                              {d.status === "requested" && (
                                <>
                                  <Button size="sm" className="h-8" onClick={() => drawAction(d.id, "fund", "Draw funded")} data-testid={`fund-draw-${d.id}`}><Check size={15} className="mr-1" /> Fund</Button>
                                  <Button size="sm" variant="outline" className="h-8 border-destructive/30 text-destructive hover:bg-destructive/10" onClick={() => drawAction(d.id, "decline", "Draw declined")} data-testid={`decline-draw-${d.id}`}><X size={15} /></Button>
                                </>
                              )}
                              {d.status === "funded" && (
                                <Button size="sm" variant="outline" className="h-8" onClick={() => drawAction(d.id, "close", "Draw marked repaid")} data-testid={`close-draw-${d.id}`}>Mark Repaid</Button>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-2 sm:grid-cols-5 gap-3">
                            <Term label="Draw Fee" value={money(p.draw_fee)} />
                            <Term label="Net Funded" value={money(p.net_funded)} />
                            <Term label={`Per ${p.payment_frequency || "period"}`} value={money(p.estimated_payment)} />
                            <Term label="# Payments" value={p.number_of_payments || "—"} />
                            <Term label="Total Payback" value={money(p.total_payback)} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>


        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-5">
          <div className="rounded-[var(--radius)] border border-border bg-card p-6">
            <div className="flex items-center gap-2 overline text-muted-foreground mb-5"><ClockCounterClockwise size={15} /> Immutable Audit Log</div>
            <div className="space-y-4">
              {audit.map((e) => (
                <div key={e.id} className="flex gap-3" data-testid={`audit-${e.event_type}`}>
                  <div className="flex flex-col items-center">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary mt-1.5" />
                    <span className="w-px flex-1 bg-border" />
                  </div>
                  <div className="pb-1">
                    <div className="text-sm">{e.description}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <span className="capitalize">{e.actor}</span> · {dateTime(e.created_at)} · {relativeTime(e.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              {audit.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={!!rejectDoc} onOpenChange={(o) => !o && setRejectDoc(null)}>
        <DialogContent className="theme-admin bg-card border-border">
          <DialogHeader><DialogTitle>Reject Document</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The merchant will see this reason and can re-upload.</p>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Statement is missing the last page." className="bg-secondary border-border" data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDoc(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitReject} data-testid="confirm-reject">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="theme-admin bg-card border-border">
          <DialogHeader><DialogTitle>Delete this deal?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This permanently removes the deal and its audit log. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="confirm-delete">Delete deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Term = ({ label, value, cap }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`num font-medium mt-0.5 ${cap ? "capitalize" : ""}`}>{value}</div>
  </div>
);
const Row = ({ l, v, accent }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={`num font-semibold ${accent ? "text-primary" : ""}`}>{v}</span></div>
);
const EngRow = ({ label, value, good, warn }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`num font-semibold ${good ? "text-primary" : warn ? "text-destructive" : ""}`}>{value}</span>
  </div>
);
